import {
    COUNTRY_TO_LOCALE,
    DEFAULT_LOCALE,
    LOCALE_COOKIE_NAME,
    LOCALE_LABELS,
    ROUTE_PATHS,
    SUPPORTED_LOCALES
} from './constants';
import { detectCountryFromIP } from './geo';

function normalizeAbsolutePath(pathname) {
    if (!pathname || pathname === '/') {
        return '/';
    }

    return pathname.startsWith('/') ? pathname : `/${pathname}`;
}

export function isSupportedLocale(locale) {
    return SUPPORTED_LOCALES.includes(locale);
}

export function normalizeLocale(locale) {
    if (!locale) {
        return DEFAULT_LOCALE;
    }

    const shortLocale = locale.toLowerCase().split('-')[0];
    return isSupportedLocale(shortLocale) ? shortLocale : DEFAULT_LOCALE;
}

export function getRoutePath(routeKeyOrPath) {
    return ROUTE_PATHS[routeKeyOrPath] || routeKeyOrPath || '/';
}

export function stripLocaleFromPathname(pathname = '/') {
    const normalizedPath = normalizeAbsolutePath(pathname);
    const segments = normalizedPath.split('/').filter(Boolean);

    if (!segments.length) {
        return '/';
    }

    if (isSupportedLocale(segments[0])) {
        const remainder = segments.slice(1).join('/');
        return remainder ? `/${remainder}` : '/';
    }

    return normalizedPath;
}

export function buildLocalizedPath(locale, routeKeyOrPath = '/') {
    const nextLocale = normalizeLocale(locale);
    const strippedPath = stripLocaleFromPathname(
        normalizeAbsolutePath(getRoutePath(routeKeyOrPath))
    );

    if (strippedPath === '/') {
        return `/${nextLocale}`;
    }

    return `/${nextLocale}${strippedPath}`;
}

export function switchLocaleInPathname(pathname, nextLocale) {
    return buildLocalizedPath(nextLocale, stripLocaleFromPathname(pathname));
}

function readLocaleCookie() {
    if (typeof document === 'undefined') {
        return null;
    }

    const cookieParts = document.cookie.split(';');
    for (const cookiePart of cookieParts) {
        const [rawKey, rawValue] = cookiePart.trim().split('=');
        if (rawKey === LOCALE_COOKIE_NAME) {
            return normalizeLocale(decodeURIComponent(rawValue || ''));
        }
    }

    return null;
}

export function persistLocale(locale) {
    if (typeof document === 'undefined') {
        return;
    }

    const normalizedLocale = normalizeLocale(locale);
    document.cookie = `${LOCALE_COOKIE_NAME}=${encodeURIComponent(normalizedLocale)}; path=/; max-age=31536000; SameSite=Lax`;
}

export function getPreferredLocale() {
    const cookieLocale = readLocaleCookie();
    if (cookieLocale) {
        return cookieLocale;
    }

    if (typeof navigator !== 'undefined') {
        const languages = navigator.languages && navigator.languages.length
            ? navigator.languages
            : [navigator.language];

        for (const browserLocale of languages) {
            const normalizedLocale = normalizeLocale(browserLocale);
            if (normalizedLocale) {
                return normalizedLocale;
            }
        }
    }

    return DEFAULT_LOCALE;
}

export function getLocaleOptions() {
    return SUPPORTED_LOCALES.map((locale) => ({
        value: locale,
        label: LOCALE_LABELS[locale]
    }));
}

/**
 * Detect locale from user's IP address via geolocation API.
 * Returns a supported locale string, or null if detection fails.
 */
export async function detectLocaleFromIP() {
    const countryCode = await detectCountryFromIP();
    if (!countryCode) return null;

    const mapped = COUNTRY_TO_LOCALE[countryCode];
    return mapped && isSupportedLocale(mapped) ? mapped : null;
}

/**
 * Async locale detection: cookie → IP geolocation → browser language → default.
 */
export async function getPreferredLocaleAsync() {
    const cookieLocale = readLocaleCookie();
    if (cookieLocale) return cookieLocale;

    const geoLocale = await detectLocaleFromIP();
    if (geoLocale) return geoLocale;

    // Fall back to synchronous browser-language detection
    return getPreferredLocale();
}